import { useHistory, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { clearArrayFromNulls } from 'helpers/clearArrayFromNulls';
import { getEventCover, getEventCountry, getEventOrganizationName } from 'helpers/eventUtils';
import { formatDate } from 'helpers/formatDate';
import { pathnames } from 'constants/pathnames';
import { EVENT_BANNER_RATIO } from 'constants/imageRatios';
import { ImageComponent } from 'components/Image';
import calendarIconMobile from 'assets/icons/calendar-mobile.svg';
import markerIconMobile from 'assets/icons/marker-mobile.svg';
import calendarIcon from 'assets/icons/calendar.svg';
import markerIcon from 'assets/icons/marker.svg';
import styles from 'pages/Calendar/EventCard/EventCard.scss';

export const EventCard = ({ event }) => {
  const {
    slug,
    type,
    title,
    description,
    organization,
    startDate,
    endDate,
    cover,
    city,
    country,
    registration,
  } = event;

  const { pathname } = useLocation();
  const { push } = useHistory();

  const pathnameParts = pathname.split('/');
  const currentDanceStyleCalendarLink = `/${clearArrayFromNulls(pathnameParts)[0]}${pathnames.calendarPage}`;

  const today = new Date();

  // The description's line-clamp depends on whether the title actually wrapped
  // to two lines, which can only be known after layout — hence the measurement.
  const titleRef = useRef(null);
  const [isTitleTwoLines, setIsTitleTwoLines] = useState(false);

  useEffect(() => {
    const titleEl = titleRef.current;
    if (!titleEl) return;

    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(titleEl).lineHeight);
      setIsTitleTwoLines(titleEl.offsetHeight > lineHeight * 1.5);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(titleEl);
    return () => observer.disconnect();
  }, [title]);

  return (
    <article className={styles.card} onClick={() => push(`${currentDanceStyleCalendarLink}/${slug}`)}>
      <div className={styles.preview}>
        <ImageComponent
          src={getEventCover(event) || 'https://placehold.co/282x353'}
          alt={cover?.alternativeText || 'cover placeholder'}
          placeholder={cover?.placeholder}
          className={styles.cover}
          fit='contain'
          ratio={EVENT_BANNER_RATIO}
        />
        <section className={styles.info}>
          <div className={styles.infoHeader}>
            <span className={styles.type}>{type}</span>
            {(today <= new Date(registration?.endDate) || today <= new Date(startDate)) &&
              typeof registration?.accept === 'boolean' && (
                <span
                  className={cn(styles.registrationStatus, { [styles.open]: registration?.accept })}
                >{`Registration is ${registration?.accept ? 'open' : 'closed'}`}</span>
              )}
          </div>
          <h3 ref={titleRef} className={styles.title}>{title}</h3>
          <p className={cn(styles.description, { [styles.descriptionShort]: isTitleTwoLines })}>{description}</p>
          {getEventOrganizationName(event) && (
            <table className={styles.organizationInfo}>
              <tbody>
                <tr>
                  <td>Organization</td>
                  <td>{getEventOrganizationName(event)}</td>
                </tr>
              </tbody>
            </table>
          )}
          <DateLocation event={{ startDate, endDate, city, country, organization }} />
        </section>
      </div>
      <DateLocation event={{ startDate, endDate, city, country, organization }} mobile />
    </article>
  );
};

const DateLocation = ({ event, mobile }) => (
  <div className={cn(styles.additionalInfo, { [styles.additionalInfoMobile]: mobile })}>
    <span>
      <img src={calendarIcon} className={styles.icon} alt='calendar' />
      <img src={calendarIconMobile} className={styles.mobileIcon} alt='calendar' />
      {formatDate(event.startDate, event.endDate)}
    </span>
    <span>
      <img src={markerIcon} className={styles.icon} alt='marker' />
      <img src={markerIconMobile} className={styles.mobileIcon} alt='marker' />
      {[event.city, getEventCountry(event)].filter(Boolean).join(', ')}
    </span>
  </div>
);
